"use client"

import { useCallback, useEffect, useState } from "react"
import { usePathname } from "next/navigation"
import Link from "next/link"
import {
  getStoredConsent,
  storeConsent,
  loadGa,
  trackPageView,
  trackEvent,
  type ConsentValue,
} from "@/lib/analytics"

/**
 * Bindet Google Analytics 4 Consent-gated ein:
 * - Config kommt serverseitig aus /settings/public/analytics (Props).
 * - Cookie-Banner erscheint nur, wenn GA aktiv ist und noch keine Wahl
 *   getroffen wurde. „Ablehnen" ist gleichwertig prominent (DSGVO).
 * - gtag lädt erst NACH Einwilligung; Pageviews bei jeder Navigation.
 * - `mk:track`-CustomEvents (z.B. aus der Buchungsstrecke) werden als
 *   GA-Events weitergereicht — ohne Einwilligung verpuffen sie.
 */
export default function AnalyticsProvider({
  gaEnabled,
  gaMeasurementId,
}: {
  gaEnabled: boolean
  gaMeasurementId: string
}) {
  const [consent, setConsent] = useState<ConsentValue>(null)
  const [bannerOpen, setBannerOpen] = useState(false)
  const pathname = usePathname()

  // Initial: gespeicherte Wahl lesen; ohne Wahl → Banner (nur wenn GA aktiv).
  useEffect(() => {
    if (!gaEnabled) return
    const stored = getStoredConsent()
    setConsent(stored)
    if (stored === null) setBannerOpen(true)
    if (stored === "granted") loadGa(gaMeasurementId)
  }, [gaEnabled, gaMeasurementId])

  // Banner über Footer-Link „Cookie-Einstellungen" wieder öffnen.
  useEffect(() => {
    const open = () => setBannerOpen(true)
    window.addEventListener("mk:consent:open", open)
    return () => window.removeEventListener("mk:consent:open", open)
  }, [])

  // Pageview bei jeder Routen-Navigation (send_page_view ist deaktiviert).
  useEffect(() => {
    if (consent === "granted" && gaEnabled) trackPageView(pathname)
  }, [pathname, consent, gaEnabled])

  // Bridge: Events aus packages/ui & Co. (CustomEvent mk:track) weiterreichen.
  useEffect(() => {
    const onTrack = (e: Event) => {
      const detail = (e as CustomEvent).detail as { name?: string; params?: Record<string, unknown> } | undefined
      if (detail?.name) trackEvent(detail.name, detail.params)
    }
    window.addEventListener("mk:track", onTrack)
    return () => window.removeEventListener("mk:track", onTrack)
  }, [])

  // Sitewide: Klicks auf Telefon-/Mail-Links als Kontakt-Engagement zählen
  // (delegiert — deckt Footer, Kontaktseite, Buchungshinweise usw. ab).
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a[href^='tel:'],a[href^='mailto:']")
      if (!a) return
      const href = a.getAttribute("href") || ""
      trackEvent("contact_click", { channel: href.startsWith("tel:") ? "telefon" : "email" })
    }
    document.addEventListener("click", onClick, { capture: true })
    return () => document.removeEventListener("click", onClick, { capture: true })
  }, [])

  const decide = useCallback(
    (value: "granted" | "denied") => {
      storeConsent(value)
      setConsent(value)
      setBannerOpen(false)
      if (value === "granted") {
        loadGa(gaMeasurementId)
        // Erster Pageview direkt nach Einwilligung.
        trackPageView(window.location.pathname)
      }
    },
    [gaMeasurementId],
  )

  if (!gaEnabled || !bannerOpen) return null

  return (
    <div
      role="dialog"
      aria-label="Cookie-Einstellungen"
      className="fixed inset-x-0 bottom-0 z-[100] border-t border-gray-200 bg-white p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.12)] dark:border-gray-700 dark:bg-gray-900"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center">
        <p className="flex-1 text-sm text-gray-700 dark:text-gray-300">
          Wir möchten mit Google Analytics verstehen, wie unsere Website genutzt wird, um sie zu
          verbessern. Die Analyse-Cookies werden nur mit deiner Einwilligung gesetzt.{" "}
          <Link href="/datenschutz" className="underline hover:text-mainColor">
            Datenschutzerklärung
          </Link>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            onClick={() => decide("denied")}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Nur notwendige
          </button>
          <button
            onClick={() => decide("granted")}
            className="rounded-lg bg-mainColor px-4 py-2 text-sm font-semibold text-white hover:brightness-90"
          >
            Alle akzeptieren
          </button>
        </div>
      </div>
    </div>
  )
}
